import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  CardapioItemPedidoDTO,
  CardapioExigenciasDoPedidoDTO,
  CardapioProdutoDTO,
  CardapioIdentificacaoDTO,
  CardapioClientePerfilDTO,
  PaisDDI,
} from '../../../../models/cardapio-digital/cardapio-digital.model';
import { CardapioClienteCacheService } from '../../../../services/cardapio-digital/cardapio-cliente-cache.service';
import { CardapioPublicoService } from '../../../../services/cardapio-digital/cardapio-publico.service';
import { PAISES_DDI } from '../../../../shared/data/paises-ddi.data';
import { TipoPedido } from '../../../../enums/TipoPedido.enum';
import { CardapioPedidoService } from '../../../../services/cardapio-digital/cardapio-pedido.service';
import { formatarPreco as formatarPrecoUtil } from '../../utils/cardapio-preco.util';
import { construirUrlImagem } from '../../utils/cardapio-imagem.util';
import { exigeConfiguracao, semEstoqueDisponivel } from '../../utils/cardapio-produto.util';
import { corrigirIconesLeaflet } from '../../../../shared/utils/leaflet-icons.util';

@Component({
  selector: 'app-cardapio-carrinho',
  templateUrl: './cardapio-carrinho.component.html',
  styleUrls: ['./cardapio-carrinho.component.scss'],
})
export class CardapioCarrinhoComponent implements OnInit, OnDestroy {
  @Input() slug = '';
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  @Input() todosOsProdutos: CardapioProdutoDTO[] = [];
  /** O que a loja exige no consumo no local (mesa e/ou comanda). */
  @Input() exigenciasDoPedido: CardapioExigenciasDoPedidoDTO | null = null;
  @Output() voltar = new EventEmitter<void>();
  /** O backend respondeu 403 (MiniShop desabilitado): o cardapio inteiro deve sair do ar. */
  @Output() lojaIndisponivel = new EventEmitter<void>();
  @Output() finalizar = new EventEmitter<void>();
  @Output() adicionarProduto = new EventEmitter<CardapioProdutoDTO>();
  @Output() adicionarProdutoRapido = new EventEmitter<CardapioProdutoDTO>();
  @Output() selecionarTipo = new EventEmitter<CardapioIdentificacaoDTO>();

  itens: CardapioItemPedidoDTO[] = [];

  // Produtos sugeridos (filtrados - nao estao no carrinho)
  produtosSugeridos: CardapioProdutoDTO[] = [];

  // Ids de sugestoes que exigem escolha: o botao mostra sliders e abre o detalhe
  sugestoesQueExigemConfiguracao = new Set<number>();

  // Ids de sugestoes sem saldo em estoque: nao exibem botao de acao
  sugestoesSemEstoque = new Set<number>();

  // Enum exposto para o template
  TipoPedido = TipoPedido;

  // Estado de animacao
  animarTotal = false;
  removingItems = new Set<CardapioItemPedidoDTO>();

  // Modal de identificacao
  modalIdentificacaoVisivel = false;
  tipoSelecionado: TipoPedido | null = null;
  modalNome = '';
  modalTelefone = '';

  // DDI selecionado (gerenciado pelo PhoneInputComponent, default: Paraguay)
  paisSelecionado: PaisDDI = PAISES_DDI[0];

  // Modal de endereco (apenas para delivery)
  modalEnderecoVisivel = false;
  enderecoRua = '';
  enderecoNumero = '';
  enderecoComplemento = '';
  enderecoReferencia = '';

  // Coordenadas do endereco selecionado no mapa
  enderecoLatitude: number | null = null;
  enderecoLongitude: number | null = null;

  // Estado de carregamento da geolocalizacao
  carregandoLocalizacao = false;

  // Estado da busca de endereco no mapa (geocodificacao direta)
  buscandoEndereco = false;

  // Mensagem de erro de geolocalizacao (inline, nao bloqueante)
  erroGeolocalizacao = '';

  // Flag para controlar se o mapa ja foi inicializado
  mapInitialized = false;

  // Variaveis privadas do Leaflet (any para evitar tipagem do import dinamico)
  private map: any = null;
  private L: any = null;
  private marker: any = null;

  // Timer de debounce para geocoding reverso
  private geocodingTimeout: any = null;

  // Numero de mesa (apenas para MESA/no local)
  modalNumeroMesa = '';
  modalNumeroComanda = '';

  // Radio RUC/CI + campo de busca
  modoBusca: 'ruc' | 'ci' = 'ruc';
  modalRuc = '';
  buscandoRuc = false;
  rucEncontrado = false;
  rucOrigem: 'interna' | 'externa' | null = null;
  private rucInput$ = new Subject<string>();
  private rucSub!: Subscription;

  // Cache de perfis de cliente (preenchimento automatico)
  perfisCliente: CardapioClientePerfilDTO[] = [];
  perfilSelecionadoIndex: number | null = null;
  enderecoCache: CardapioClientePerfilDTO['endereco'] | undefined;

  private sub!: Subscription;

  constructor(
    public pedidoService: CardapioPedidoService,
    private clienteCacheService: CardapioClienteCacheService,
    private cardapioPublicoService: CardapioPublicoService,
  ) {}

  ngOnInit(): void {
    this.sub = this.pedidoService.itensObservable.subscribe(itens => {
      this.itens = itens;
      this.atualizarSugestoes();
    });

    // Debounce da busca de RUC (1500ms apos parar de digitar)
    this.rucSub = this.rucInput$.pipe(
      debounceTime(1500),
      distinctUntilChanged(),
      switchMap(ruc => {
        if (!ruc || ruc.length < 3 || !this.slug) {
          this.buscandoRuc = false;
          this.rucEncontrado = false;
          this.rucOrigem = null;
          return of(null);
        }
        this.buscandoRuc = true;
        this.rucEncontrado = false;
        this.rucOrigem = null;
        return this.cardapioPublicoService.buscarDocumento(this.slug, ruc, this.modoBusca).pipe(
          catchError((err: unknown) => {
            if (err instanceof HttpErrorResponse && err.status === 403) {
              this.lojaIndisponivel.emit();
            }
            return of(null);
          })
        );
      })
    ).subscribe(resultado => {
      this.buscandoRuc = false;
      if (resultado) {
        this.rucEncontrado = true;
        this.rucOrigem = resultado.origem;
        this.modalRuc = resultado.ruc;
        // Preencher apenas campos vazios (preservar dados ja informados pelo usuario)
        if (!this.modalNome.trim()) {
          this.modalNome = resultado.nome;
        }
        if (resultado.telefone && !this.modalTelefone.trim()) {
          if (resultado.ddi) {
            const ddiComPlus = resultado.ddi.startsWith('+') ? resultado.ddi : `+${resultado.ddi}`;
            const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
            if (paisEncontrado) {
              this.paisSelecionado = paisEncontrado;
            }
          }
          this.modalTelefone = resultado.telefone;
        }
      }
    });
  }

  get total(): number {
    return this.pedidoService.getTotalPedido();
  }

  get totalFormatado(): string {
    return this.formatarPreco(this.total);
  }

  /** Formata as opcoes selecionadas de um item para exibicao */
  formatarOpcoes(item: CardapioItemPedidoDTO): string[] {
    const resultado: string[] = [];
    if (item.opcoesSelecionadas) {
      for (const grupo of item.opcoesSelecionadas) {
        for (const opcao of grupo.itens) {
          if (opcao.quantidade > 1) {
            resultado.push(`${opcao.quantidade}x ${opcao.titulo}`);
          } else {
            resultado.push(opcao.titulo);
          }
        }
      }
    }
    return resultado;
  }

  formatarPrecoItem(valor: number): string {
    return this.formatarPreco(valor);
  }

  /** URLs de imagem que falharam ao carregar -> exibem o placeholder. */
  private readonly urlsComErro = new Set<string>();

  marcarUrlErro(url: string | undefined): void {
    if (url) this.urlsComErro.add(url);
  }

  urlComErro(url: string | undefined): boolean {
    return !!url && this.urlsComErro.has(url);
  }

  /** Preco unitario de um item (base + opcoes) */
  precoUnitario(item: CardapioItemPedidoDTO): number {
    return item.valorTotal / item.quantidade;
  }

  /**
   * Resolve a URL da imagem de um item do carrinho garantindo a MESMA
   * construcao usada nos cards do cardapio e nas sugestoes.
   *
   * O carrinho e persistido em localStorage com o produto serializado.
   * O campo `produto.foto` salvo pode estar desatualizado (ex.: somente o
   * nome do arquivo, sem o prefixo do host) por ter sido gravado em uma
   * sessao anterior. Por isso, em vez de confiar no valor persistido:
   *
   * 1. Procura o produto correspondente em `todosOsProdutos` (lista ja
   *    processada com URLs completas) e usa a foto de la — identica a do
   *    card/sugestao.
   * 2. Se nao encontrar (ex.: item copiado do historico), normaliza a foto
   *    do proprio item: se ja for URL absoluta retorna como esta; se for so
   *    o nome do arquivo, aplica o mesmo prefixo do catalogo.
   */
  imagemDoItem(item: CardapioItemPedidoDTO): string | null {
    const correspondente = this.todosOsProdutos.find(p => p.id === item.produto.id);
    if (correspondente?.foto) {
      return correspondente.foto;
    }
    return construirUrlImagem(item.produto.foto, this.prefixoImagem());
  }

  /**
   * Deriva o prefixo de imagem (`.../{idEmpresa}/item`) a partir de uma foto
   * ja processada do catalogo, removendo o nome do arquivo. Mantem a fonte
   * unica da verdade sem precisar replicar `environment.imgHost`/idEmpresa.
   */
  private prefixoImagem(): string {
    const fotoCatalogo = this.todosOsProdutos.find(p => !!p.foto)?.foto;
    if (!fotoCatalogo) return '';
    const barra = fotoCatalogo.lastIndexOf('/');
    return barra > 0 ? fotoCatalogo.substring(0, barra) : '';
  }

  /** Calcula o percentual de desconto de um produto */
  calcularDesconto(produto: CardapioProdutoDTO): number | null {
    if (produto.percentualDesconto) return produto.percentualDesconto;
    if (produto.precoPromocional && produto.preco > produto.precoPromocional) {
      return Math.round((1 - produto.precoPromocional / produto.preco) * 100);
    }
    return null;
  }

  /** Retorna o preco de exibicao do produto (promocional se houver) */
  precoExibicao(produto: CardapioProdutoDTO): number {
    return produto.precoPromocional ?? produto.preco;
  }

  /** Saldo disponivel em estoque do produto (null quando nao controla estoque). */
  private saldoDoProduto(produtoId: number): number | null {
    const completo = this.todosOsProdutos.find(p => p.id === produtoId);
    const saldo = completo?.saldoDisponivel;
    return saldo ?? null;
  }

  /**
   * Indica se ainda e possivel incrementar este item respeitando o estoque:
   * a quantidade TOTAL do produto no carrinho deve ser menor que o saldo.
   * Quando o produto nao controla estoque, limita apenas ao teto de 99.
   */
  podeIncrementarItem(item: CardapioItemPedidoDTO): boolean {
    if (item.quantidade >= 99) return false;
    const saldo = this.saldoDoProduto(item.produto.id);
    if (saldo == null) return true;
    return this.pedidoService.quantidadePorProduto(item.produto.id) < Math.floor(saldo);
  }

  incrementarItem(item: CardapioItemPedidoDTO): void {
    if (this.podeIncrementarItem(item)) {
      this.pedidoService.incrementItem(item);
      this.triggerAnimacao();
    }
  }

  decrementarItem(item: CardapioItemPedidoDTO): void {
    if (item.quantidade > 1) {
      this.pedidoService.decrementItem(item);
      this.triggerAnimacao();
    }
  }

  onRemover(item: CardapioItemPedidoDTO): void {
    this.removingItems.add(item);
    this.triggerAnimacao();

    // Aguardar a animacao scale-out-top (0.5s) e remover o item
    setTimeout(() => {
      this.pedidoService.removeItem(item);
      this.removingItems.delete(item);
    }, 500);
  }

  onVoltar(): void {
    this.voltar.emit();
  }

  onFinalizar(): void {
    this.finalizar.emit();
  }

  /** Chamado quando o usuario alterna entre RUC e CI */
  onModoBuscaChange(): void {
    this.modalRuc = '';
    this.rucEncontrado = false;
    this.rucOrigem = null;
    this.buscandoRuc = false;
    // Cancelar debounce pendente para evitar busca com modo errado
    this.rucInput$.next('');
  }

  /** Chamado quando o usuario digita no campo RUC/CI */
  onRucInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const filtrado = (input.value || '').replace(/[^a-zA-Z0-9\-]/g, '');
    input.value = filtrado;
    this.modalRuc = filtrado;
    this.rucInput$.next(filtrado.trim());
  }

  onSelecionarTipo(tipo: TipoPedido): void {
    this.tipoSelecionado = tipo;
    this.modalNome = '';
    this.modalTelefone = '';
    this.modalNumeroMesa = '';
    this.modalNumeroComanda = '';
    this.modoBusca = 'ruc';
    this.modalRuc = '';
    this.rucEncontrado = false;
    this.rucOrigem = null;
    this.perfilSelecionadoIndex = null;
    this.enderecoCache = undefined;

    // Carregar perfis salvos para o slug atual
    this.perfisCliente = this.clienteCacheService.getPerfis(this.slug);

    // Auto-selecionar o perfil mais recente (indice 0) se houver perfis salvos
    if (this.perfisCliente.length > 0) {
      this.onSelecionarPerfil(0);
    }

    this.modalIdentificacaoVisivel = true;
  }

  get isMesaSelecionado(): boolean {
    return this.tipoSelecionado === TipoPedido.MESA;
  }

  /**
   * Mesa e comanda no consumo no local: cada campo so existe quando a loja o exige. O
   * interruptor da configuracao decide as duas coisas de uma vez — aparece porque e obrigatorio.
   */
  get exigeNumeroDaMesa(): boolean {
    return this.isMesaSelecionado && this.exigenciasDoPedido?.numeroDaMesa === true;
  }

  get exigeNumeroDaComanda(): boolean {
    return this.isMesaSelecionado && this.exigenciasDoPedido?.numeroDaComanda === true;
  }

  onConfirmarIdentificacao(): void {
    // Nome, telefone e, no consumo no local, mesa/comanda que a loja exige.
    if (!this.modalFormValido || !this.tipoSelecionado) return;

    // Se for delivery, abrir modal de endereco antes de emitir
    if (this.tipoSelecionado === TipoPedido.DELIVERY) {
      this.modalIdentificacaoVisivel = false;

      // Pre-preencher endereco com cache do perfil selecionado (se disponivel)
      if (this.enderecoCache) {
        this.enderecoRua = this.enderecoCache.rua;
        this.enderecoNumero = this.enderecoCache.numero;
        this.enderecoComplemento = this.enderecoCache.complemento || '';
        this.enderecoReferencia = this.enderecoCache.referencia || '';
      } else {
        this.enderecoRua = '';
        this.enderecoNumero = '';
        this.enderecoComplemento = '';
        this.enderecoReferencia = '';
      }

      this.modalEnderecoVisivel = true;

      // Pre-preencher coordenadas do cache (se disponivel)
      if (this.enderecoCache?.latitude && this.enderecoCache?.longitude) {
        this.enderecoLatitude = this.enderecoCache.latitude;
        this.enderecoLongitude = this.enderecoCache.longitude;
      } else {
        this.enderecoLatitude = null;
        this.enderecoLongitude = null;
      }

      // Inicializar mapa apos o DOM renderizar o dialog
      this.erroGeolocalizacao = '';
      setTimeout(() => this.inicializarMapa(), 300);
      return;
    }

    const dados: CardapioIdentificacaoDTO = {
      tipo: this.tipoSelecionado,
      nome: this.modalNome.trim(),
      telefone: this.telefoneDoPedido,
      paisDDI: this.paisSelecionado,
      numeroMesa: this.isMesaSelecionado ? this.modalNumeroMesa.trim() : undefined,
      numeroComanda: this.isMesaSelecionado ? this.modalNumeroComanda.trim() : undefined,
      ruc: this.modoBusca === 'ruc' && this.modalRuc.trim() ? this.modalRuc.trim() : undefined,
      ci: this.modoBusca === 'ci' && this.modalRuc.trim() ? this.modalRuc.trim() : undefined,
    };

    this.modalIdentificacaoVisivel = false;
    this.selecionarTipo.emit(dados);
  }

  /** Voltar do modal de endereco para o modal de identificacao */
  onVoltarEndereco(): void {
    this.destruirMapa();
    this.modalEnderecoVisivel = false;
    this.modalIdentificacaoVisivel = true;
  }

  onFecharModalEndereco(): void {
    this.destruirMapa();
    this.modalEnderecoVisivel = false;
  }

  onConfirmarEndereco(): void {
    if (!this.enderecoRua.trim() || !this.tipoSelecionado) return;

    const dados: CardapioIdentificacaoDTO = {
      tipo: this.tipoSelecionado,
      nome: this.modalNome.trim(),
      telefone: this.telefoneDoPedido,
      paisDDI: this.paisSelecionado,
      rua: this.enderecoRua.trim(),
      numero: this.enderecoNumero.trim(),
      complemento: this.enderecoComplemento.trim() || undefined,
      referencia: this.enderecoReferencia.trim() || undefined,
      latitude: this.enderecoLatitude || undefined,
      longitude: this.enderecoLongitude || undefined,
      ruc: this.modoBusca === 'ruc' && this.modalRuc.trim() ? this.modalRuc.trim() : undefined,
      ci: this.modoBusca === 'ci' && this.modalRuc.trim() ? this.modalRuc.trim() : undefined,
    };

    this.destruirMapa();
    this.modalEnderecoVisivel = false;
    this.selecionarTipo.emit(dados);
  }

  get enderecoFormValido(): boolean {
    return this.enderecoRua.trim().length > 0;
  }

  onFecharModal(): void {
    this.modalIdentificacaoVisivel = false;
  }

  get isDeliverySelecionado(): boolean {
    return this.tipoSelecionado === TipoPedido.DELIVERY;
  }

  // Mesmas regras do pedido publico no backend (CardapioPedidoPublicoDTO): nome
  // de 3 a 220 caracteres e telefone obrigatorio, com 8 a 20 caracteres contando
  // o DDI. Validar aqui evita que o pedido seja recusado so no ultimo passo.
  private readonly NOME_MINIMO = 3;
  private readonly TELEFONE_MINIMO = 8;
  private readonly TELEFONE_MAXIMO = 20;

  /** Telefone como vai ao pedido: DDI + somente os digitos digitados. */
  private get telefoneDoPedido(): string {
    return this.paisSelecionado.ddi + this.modalTelefone.replace(/\D/g, '');
  }

  /** Nome digitado, mas curto demais (o vazio so desabilita o botao). */
  get nomeCurto(): boolean {
    const tamanho = this.modalNome.trim().length;
    return tamanho > 0 && tamanho < this.NOME_MINIMO;
  }

  /** Telefone digitado, mas fora do tamanho aceito (o vazio so desabilita o botao). */
  get telefoneIncompleto(): boolean {
    if (!this.modalTelefone.replace(/\D/g, '')) return false;
    const tamanho = this.telefoneDoPedido.length;
    return tamanho < this.TELEFONE_MINIMO || tamanho > this.TELEFONE_MAXIMO;
  }

  get modalFormValido(): boolean {
    const nomeValido = this.modalNome.trim().length >= this.NOME_MINIMO;
    const telefoneValido = !!this.modalTelefone.replace(/\D/g, '') && !this.telefoneIncompleto;
    if (!nomeValido || !telefoneValido) return false;
    if (this.isDeliverySelecionado) return true;
    if (this.exigeNumeroDaMesa && !this.modalNumeroMesa.trim()) return false;
    if (this.exigeNumeroDaComanda && !this.modalNumeroComanda.trim()) return false;
    return true;
  }

  /** Clique no corpo do card sugerido: sempre abre o detalhe do produto. */
  onAdicionarProduto(produto: CardapioProdutoDTO): void {
    this.adicionarProduto.emit(produto);
  }

  /**
   * Botao do card sugerido. Impede a propagacao (senao o corpo do card tambem
   * abriria o detalhe): produto sem escolha obrigatoria vai direto ao carrinho
   * e o cliente permanece no carrinho, produto que exige configuracao abre o
   * detalhe.
   */
  onAcaoSugestao(event: Event, produto: CardapioProdutoDTO): void {
    event.stopPropagation();

    if (this.sugestoesQueExigemConfiguracao.has(produto.id)) {
      this.adicionarProduto.emit(produto);
      return;
    }

    this.adicionarProdutoRapido.emit(produto);
  }

  /** Seleciona um perfil salvo e preenche os campos do modal */
  onSelecionarPerfil(index: number): void {
    const perfil = this.perfisCliente[index];
    if (!perfil) return;

    this.perfilSelecionadoIndex = index;
    this.modalNome = perfil.nome;
    this.paisSelecionado = perfil.paisDDI;
    this.modalTelefone = perfil.telefone;

    // Restaurar RUC ou CI do perfil salvo
    if (perfil.ruc) {
      this.modoBusca = 'ruc';
      this.modalRuc = perfil.ruc;
    } else if (perfil.ci) {
      this.modoBusca = 'ci';
      this.modalRuc = perfil.ci;
    } else {
      this.modoBusca = 'ruc';
      this.modalRuc = '';
    }
    this.rucEncontrado = false;
    this.rucOrigem = null;

    // Guardar endereco para pre-preencher modal de endereco (se delivery)
    this.enderecoCache = perfil.endereco;
  }

  /** Limpa campos para digitar novo perfil */
  onNovoPerfil(): void {
    this.perfilSelecionadoIndex = null;
    this.modalNome = '';
    this.modalTelefone = '';
    this.modoBusca = 'ruc';
    this.modalRuc = '';
    this.rucEncontrado = false;
    this.rucOrigem = null;
    this.paisSelecionado = PAISES_DDI[0];
    this.enderecoCache = undefined;
  }

  /** Chamado quando o dropdown de perfis muda de valor */
  onDropdownPerfilChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const valor = select.value;
    if (valor === '') {
      this.onNovoPerfil();
    } else {
      this.onSelecionarPerfil(Number(valor));
    }
  }

  /** Formata label do perfil para exibicao no dropdown: "Nome DDI...ultimos3" */
  formatarChipPerfil(perfil: CardapioClientePerfilDTO): string {
    const tel = perfil.telefone;
    const ultimos3 = tel.slice(-3);
    return `${perfil.nome} ${perfil.paisDDI.ddi}...${ultimos3}`;
  }

  trackByItem(index: number, item: CardapioItemPedidoDTO): string {
    return `${item.produto.id}-${JSON.stringify(item.opcoesSelecionadas)}`;
  }

  trackByOpcao(index: number, opcao: string): string {
    return opcao;
  }

  trackByProduto(index: number, produto: CardapioProdutoDTO): number {
    return produto.id;
  }

  private formatarPreco(valor: number): string {
    return formatarPrecoUtil(valor, this.moedaSimbolo, this.moedaLocal);
  }

  private triggerAnimacao(): void {
    this.animarTotal = true;
    setTimeout(() => {
      this.animarTotal = false;
    }, 900);
  }

  /**
   * Filtra produtos que NAO estao no carrinho para a secao de sugestoes e
   * pre-calcula as duas regras usadas pelo botao de acao. Pre-calcular evita
   * reavaliar a regra de cada card a cada ciclo de deteccao de mudanca.
   *
   * A quantidade no carrinho e sempre 0 aqui: o que ja esta no carrinho e
   * justamente o que sai da lista de sugestoes.
   */
  private atualizarSugestoes(): void {
    const idsNoCarrinho = new Set(this.itens.map(i => i.produto.id));
    this.produtosSugeridos = this.todosOsProdutos.filter(p => !idsNoCarrinho.has(p.id));

    this.sugestoesQueExigemConfiguracao = new Set(
      this.produtosSugeridos.filter(p => exigeConfiguracao(p)).map(p => p.id)
    );
    this.sugestoesSemEstoque = new Set(
      this.produtosSugeridos.filter(p => semEstoqueDisponivel(p)).map(p => p.id)
    );
  }

  // --- Metodos do Mapa Leaflet ---

  /** Inicializa o mapa Leaflet com import dinamico (padrao Mini Shop) */
  async inicializarMapa(): Promise<void> {
    const mapContainer = document.getElementById('map-endereco-leaflet');
    if (!mapContainer) return;

    // Limpeza: se o mapa ja existe, destruir antes de recriar
    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    // Carregar Leaflet via import dinamico (lazy loading)
    // Em builds de producao com esbuild o modulo ESM retorna { default: L },
    // entao e necessario extrair o .default quando presente.
    if (!this.L) {
      const leafletModule = await import('leaflet');
      this.L = (leafletModule as any).default ?? leafletModule;
      this.fixLeafletIcons();
    }

    // Determinar centro do mapa
    let centro: [number, number] = [-25.776104, -55.074783]; // Paraguai (padrao)
    if (this.enderecoLatitude && this.enderecoLongitude) {
      centro = [this.enderecoLatitude, this.enderecoLongitude];
    }

    // Criar mapa
    this.map = this.L.map('map-endereco-leaflet', { zoomControl: true }).setView(centro, 15);
    this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(this.map);

    // Marcador arrastavel
    this.marker = this.L.marker(centro, { draggable: true, autoPan: true }).addTo(this.map);

    // Evento: fim do arrasto do marcador
    this.marker.on('dragend', () => {
      const pos = this.marker.getLatLng();
      this.enderecoLatitude = pos.lat;
      this.enderecoLongitude = pos.lng;
      this.geocodificarReversoComDebounce(pos.lat, pos.lng);
    });

    // Evento: clique no mapa para mover marcador
    this.map.on('click', (e: any) => {
      const lat = e.latlng.lat;
      const lng = e.latlng.lng;
      this.enderecoLatitude = lat;
      this.enderecoLongitude = lng;
      this.marker.setLatLng([lat, lng]);
      this.geocodificarReversoComDebounce(lat, lng);
    });

    this.mapInitialized = true;

    // Ajuste de renderizacao
    setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 250);

    // Solicitar geolocalizacao automaticamente ao abrir o modal
    // (apenas se ainda nao tem coordenadas salvas)
    if (!this.enderecoLatitude && !this.enderecoLongitude) {
      this.onUsarMinhaLocalizacao();
    }
  }

  private fixLeafletIcons(): void {
    corrigirIconesLeaflet(this.L);
  }

  /** Solicitar geolocalizacao GPS do navegador */
  onUsarMinhaLocalizacao(): void {
    if (!navigator.geolocation) {
      this.erroGeolocalizacao = 'cardapio_digital.checkout.endereco_erro_geolocalizacao';
      return;
    }

    this.carregandoLocalizacao = true;
    this.erroGeolocalizacao = '';

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.enderecoLatitude = pos.coords.latitude;
        this.enderecoLongitude = pos.coords.longitude;
        this.centralizarMapa(pos.coords.latitude, pos.coords.longitude);
        this.geocodificarReverso(pos.coords.latitude, pos.coords.longitude);
        this.carregandoLocalizacao = false;
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          this.erroGeolocalizacao = 'cardapio_digital.checkout.endereco_erro_geolocalizacao_negada';
        } else {
          this.erroGeolocalizacao = 'cardapio_digital.checkout.endereco_erro_geolocalizacao';
        }
        this.carregandoLocalizacao = false;
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  /**
   * Geocodificação DIRETA (rua → coordenadas): busca o endereço digitado no
   * Nominatim e destaca o ponto no mapa, permitindo pesquisar por rua em vez de
   * só arrastar o pino. Complementa a geocodificação reversa (mapa → endereço).
   */
  async buscarEnderecoNoMapa(): Promise<void> {
    const termo = [this.enderecoRua, this.enderecoNumero]
      .map((v) => (v || '').trim())
      .filter((v) => v.length > 0)
      .join(', ');
    if (!termo) return;

    this.buscandoEndereco = true;
    this.erroGeolocalizacao = '';
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(termo)}&format=json&addressdetails=1&limit=1`;
      const resp = await fetch(url, { headers: { 'Accept-Language': 'es' } });
      const data = await resp.json();
      if (Array.isArray(data) && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        if (!isNaN(lat) && !isNaN(lng)) {
          this.enderecoLatitude = lat;
          this.enderecoLongitude = lng;
          this.centralizarMapa(lat, lng);
        }
      } else {
        this.erroGeolocalizacao = 'cardapio_digital.checkout.endereco_nao_encontrado';
      }
    } catch {
      // Silencioso: o cliente pode ajustar o pino manualmente.
    } finally {
      this.buscandoEndereco = false;
    }
  }

  /** Centralizar mapa e mover marcador para novas coordenadas */
  private centralizarMapa(lat: number, lng: number): void {
    if (this.map) {
      this.map.setView([lat, lng], 16);
    }
    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    }
  }

  /** Geocoding reverso com debounce de 500ms */
  private geocodificarReversoComDebounce(lat: number, lng: number): void {
    clearTimeout(this.geocodingTimeout);
    this.geocodingTimeout = setTimeout(() => {
      this.geocodificarReverso(lat, lng);
    }, 500);
  }

  /** Geocoding reverso via Nominatim (OpenStreetMap) */
  private async geocodificarReverso(lat: number, lng: number): Promise<void> {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;
      const resp = await fetch(url, {
        headers: { 'Accept-Language': 'es' }
      });
      const data = await resp.json();
      if (data.address) {
        this.enderecoRua = data.address.road || data.display_name || '';
        if (data.address.house_number) {
          this.enderecoNumero = data.address.house_number;
        }
      }
    } catch {
      // Silencioso: usuario pode preencher manualmente
    }
  }

  /** Destruir mapa Leaflet (cleanup para evitar memory leak) */
  private destruirMapa(): void {
    clearTimeout(this.geocodingTimeout);
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.marker = null;
    this.mapInitialized = false;
  }

  /**
   * Garante que o Leaflet recalcule o tamanho do mapa quando a
   * janela e redimensionada (ex.: usuario atravessa o breakpoint
   * 768px com o modal de endereco aberto, trocando entre layout
   * de 1 e 2 colunas).
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.map && this.modalEnderecoVisivel) {
      setTimeout(() => this.map && this.map.invalidateSize(), 150);
    }
  }

  ngOnDestroy(): void {
    this.destruirMapa();
    this.sub?.unsubscribe();
    this.rucSub?.unsubscribe();
  }
}
